import { useCallback, useMemo, useState } from 'react';
import { Tag } from '@/shared/ui/Tag';
import { TagCombobox, normalizeTagInput } from '@/features/report/Tags';
import { useSettings } from '@/features/settings';
import styles from './WorklistTagFilter.module.css';

interface WorklistTagFilterProps {
  /** Canonical UPPER_SNAKE_CASE values currently selected. */
  value: string[];
  onChange: (next: string[]) => void;
  /** Catalog of available tag classes. */
  options?: string[];
}

/**
 * Worklist-side tag filter. Reuses the worksheet's TagCombobox + Tag pill so
 * the chrome reads identically across the app. Filtering only — typed values
 * that don't match the catalog are ignored (no "Create" row, no free-form).
 */
export default function WorklistTagFilter({
  value,
  onChange,
  options = [],
}: WorklistTagFilterProps) {
  const { settings } = useSettings();
  const [draft, setDraft] = useState('');

  const selectedSet = useMemo(() => new Set(value), [value]);
  const needle = useMemo(() => normalizeTagInput(draft), [draft]);

  const suggestions = useMemo(() => {
    const pool = options.filter((entry) => !selectedSet.has(entry));
    if (!needle) return pool;
    return pool.filter((entry) => entry.includes(needle));
  }, [needle, options, selectedSet]);

  const commit = useCallback(
    (text: string) => {
      const normalized = normalizeTagInput(text);
      if (!normalized) return;
      if (selectedSet.has(normalized)) {
        setDraft('');
        return;
      }
      if (!options.includes(normalized)) return;
      onChange([...value, normalized]);
      setDraft('');
    },
    [onChange, options, selectedSet, value]
  );

  const removeAt = useCallback(
    (tag: string) => {
      onChange(value.filter((v) => v !== tag));
    },
    [onChange, value]
  );

  const removeLast = useCallback(() => {
    if (value.length === 0) return;
    onChange(value.slice(0, -1));
  }, [onChange, value]);

  return (
    <div className={styles.container}>
      {value.length > 0 && (
        <div className={styles.pills}>
          {value.map((tag) => (
            <Tag
              key={tag}
              value={tag}
              compact={settings.compactMode}
              onRemove={removeAt}
            />
          ))}
        </div>
      )}
      <TagCombobox
        draft={draft}
        onDraftChange={setDraft}
        suggestions={suggestions}
        onCommit={commit}
        createValue={null}
        onRemoveLast={removeLast}
        onClearDraft={() => setDraft('')}
      />
    </div>
  );
}
